import { Checkbox } from '@almbrand/checkbox';
import { LoadingSpinner } from '@almbrand/loadingspinner';
import classNames from 'classnames';
import { ThemeNames } from 'constantsValues';
import { PageContext } from 'context/PageContext';
import { useModal, useSpdSearch } from 'hooks';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, UseFormRegister, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { Obj } from 'src/hooks/useSpdSearch';
import { useAppDispatch } from 'store/hooks';
import { debounce, getResponseValueByParam } from 'utilities';
import { applyBrandFlowStopField, clearBrandFlowStopField } from '../../../services/brandFlowStop';
import {
	applyExpensiveCarFields,
	clearExpensiveCarFields,
	readExpensiveCarConfig,
} from '../../../services/expensiveCarCategory';
import {
	clearCarGroupResult,
	persistCarGroupResult,
	runVehicleLookupChain,
} from '../../../services/VehicleLookupService';
import { CarMakeAndModel } from './CarMakeAndModel';
import styles from './CarSearch.module.scss';
import { LicensePlateInput } from './LicensePlateInput';
import { ResultBox } from './ResultBox';
import { getCarSearchIsValidFieldName, isSearchResultValid, setCarSearchIsValid } from './validateCarSearch';

export type Option = {
	value: string;
	label: string;
};

export type ResultBoxProps = {
	header: string;
	subHeader: string;
};

export interface CarSearchProps {
	register: UseFormRegister<FieldValues>;
	trigger?: UseFormTrigger<FieldValues>;
	setValue?: UseFormSetValue<FieldValues>;
	submitStep?: () => void;
	contentType: string;
	stepContentId: number;
	onComplete?: (licensePlate: string) => void;
	resultBox?: ResultBoxProps | null;
	licensePlateSearchField: SpdSearchType;
	makeSearchField: SpdSearchType;
	yearSearchField: SpdSearchType;
	modelSearchField: SpdSearchType;
	variantSearchField: SpdSearchType;
}

export const CarSearch: React.FC<CarSearchProps> = ({
	register,
	trigger,
	setValue,
	submitStep,
	contentType,
	stepContentId,
	onComplete,
	licensePlateSearchField,
	makeSearchField,
	yearSearchField,
	modelSearchField,
	variantSearchField,
	...props
}) => {
	const pageContext = useContext(PageContext);
	const { theme } = pageContext ?? {};
	const themeName = ThemeNames[theme];
	const { displayErrorModal } = useModal();
	const dispatch = useAppDispatch();

	const licensePlateSearch = useSpdSearch({ spdSearchType: licensePlateSearchField });

	const [showSpinner, setShowSpinner] = useState(false);
	const [showResult, setShowResult] = useState(false);
	const [noLicensePlate, setNoLicensePlate] = useState(false);
	const [resultBox, setResultBox] = useState<ResultBoxProps | null>(null);
	const [licensePlateHasError, setLicensePlateHasError] = useState(false);
	// Increments on every change; async REGNO handlers bail if a newer change has arrived.
	const licensePlateRequestIdRef = useRef(0);
	const carSearchIsValidFieldName = getCarSearchIsValidFieldName(stepContentId);

	const updateCarSearchIsValid = (isValid: boolean) => {
		setCarSearchIsValid({
			fieldName: carSearchIsValidFieldName,
			isValid,
			register,
			setValue,
			trigger,
		});
	};

	useEffect(() => {
		updateCarSearchIsValid(false);

		return () => {
			// Match handleLicensePlateChange / handleCheckboxChange: bump the id so any
			// already-awaiting runRegnoLookup exits before it can setState on an unmounted component.
			licensePlateRequestIdRef.current += 1;
			debouncedRegnoLookup.cancel();
		};
	}, []);

	// Show ResultBox and hide spinner when resultBox is set from parent
	useEffect(() => {
		if (resultBox) {
			setShowResult(true);
			setShowSpinner(false);
		}
	}, [resultBox]);

	const handleLicensePlateChange = async (formatted: string) => {
		// Remove all spaces and make lower case before sending to parent
		const normalized = formatted.replace(/\s+/g, '').toLowerCase();

		// Any change invalidates in-flight lookups; capture our id and bail if superseded.
		const requestId = ++licensePlateRequestIdRef.current;
		// check if current request has been superseded by a newer one.
		const isStale = () => licensePlateRequestIdRef.current !== requestId;

		debouncedRegnoLookup.cancel();

		setShowResult(false);
		setResultBox(null);
		setLicensePlateHasError(false);
		updateCarSearchIsValid(false);
		clearCarGroupResult(dispatch);
		clearExpensiveCarFields(saveFormValue);
		clearBrandFlowStopField({ saveFormValue, dispatch });

		if (!normalized) {
			setShowSpinner(false);
			return;
		}

		debouncedRegnoLookup(normalized, isStale);
		onComplete?.(normalized);
	};

	const resetOnLookupError = () => {
		setShowSpinner(false);
		setShowResult(false);
		setResultBox(null);
		updateCarSearchIsValid(false);
	};

	const showLookupErrorModal = () => {
		displayErrorModal(
			'Vi kunne ikke hente bilens oplysninger',
			'',
			'Prøv igen om lidt, eller indtast bilens oplysninger manuelt.'
		);
	};

	const runRegnoLookup = async (normalized: string, isStale: () => boolean) => {
		if (isStale()) return;
		setShowSpinner(true);

		let result: Obj | undefined;
		try {
			result = (await licensePlateSearch.doSearch({
				name: licensePlateSearchField.searchConfiguration.spdSearchConfigurationName,
				params: [
					{
						param_name:
							licensePlateSearchField.searchConfiguration.inputFields.items[0].content.param || '',
						param_value: normalized.toLocaleUpperCase(),
					},
					{
						param_name: 'USER_ID',
						param_value: ' ',
					},
				],
			})) as Obj | undefined;
		} catch (error) {
			if (isStale()) return;
			console.error('REGNO lookup failed:', error);
			resetOnLookupError();
			showLookupErrorModal();
			return;
		}
		if (isStale()) return;

		if (!isSearchResultValid(licensePlateSearchField.searchConfiguration, result as Obj)) {
			resetOnLookupError();
			setLicensePlateHasError(true);
			return;
		}

		if (
			applyBrandFlowStopField({
				brandName: (result as Obj).make?.toString(),
				saveFormValue,
				dispatch,
				contentType,
				page: String(stepContentId),
			})
		) {
			resetOnLookupError();
			return;
		}

		try {
			const { variantDetails, cardtl } = await runVehicleLookupChain(licensePlateSearch.url, result as Obj);
			if (isStale()) return;
			saveOutputFieldsToForm(
				register,
				variantSearchField.secondarySearchConfiguration,
				variantDetails,
				contentType
			);
			persistCarGroupResult({
				dispatch,
				contentType,
				page: String(stepContentId),
				cardtl,
			});
			applyExpensiveCarFields({
				variantDetails,
				ageInput: {
					kind: 'registrationDate',
					value: (result as Obj).vehicleRegistrationDate as string | undefined,
				},
				config: readExpensiveCarConfig(pageContext?.metaArea?.items?.[0]?.content?.staticFields),
				saveFormValue,
			});
		} catch (error) {
			if (isStale()) return;
			console.error('Vehicle lookup chain failed:', error);
			resetOnLookupError();
			showLookupErrorModal();
			return;
		}

		if (isStale()) return;
		setResultBox({
			header: `${(result as Obj).make} ${(result as Obj).model}`,
			subHeader: `${(result as Obj).modelYear} ${(result as Obj).variant}`,
		});

		saveFormValue(licensePlateSearchField.uniqueIdentifier.uid, normalized.toLocaleUpperCase());
		saveOutputFieldsToForm(register, licensePlateSearchField.searchConfiguration, result as Obj, contentType, true);
	};

	// Stable debounced wrapper that always dispatches to the latest runRegnoLookup closure.
	const runRegnoLookupRef = useRef(runRegnoLookup);
	runRegnoLookupRef.current = runRegnoLookup;
	const debouncedRegnoLookup = useMemo(
		() => debounce((n: string, s: () => boolean) => runRegnoLookupRef.current(n, s), 600),
		[]
	);

	const saveFormValue = (fieldName: string, value: unknown) => {
		const { onChange, name } = register(fieldName, { required: true });
		const formValue = {
			id: name,
			value,
			type: contentType,
		};

		if (setValue) {
			setValue(name, formValue, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
			return;
		}

		onChange({
			target: {
				name,
				value: formValue,
			},
		});
	};

	const saveOutputFieldsToForm = (
		register: UseFormRegister<FieldValues>,
		searchConfiguration: SpdSearchType['searchConfiguration'],
		value: Option | Obj,
		contentType: string,
		completeCarSearch?: boolean
	) => {
		searchConfiguration.outputFields.items.forEach((outputField) => {
			const mappedValue = value.value ?? getResponseValueByParam(value as Obj, outputField.content.param);
			saveFormValue(outputField.content.uniqueIdentifier.uid, mappedValue);
		});

		if (completeCarSearch) {
			updateCarSearchIsValid(isSearchResultValid(searchConfiguration, value));
		}
	};

	const handleCheckboxChange = async () => {
		// Invalidate any in-flight REGNO chain before switching modes so it can't overwrite
		// the make/model selection when it eventually resolves.
		licensePlateRequestIdRef.current += 1;
		debouncedRegnoLookup.cancel();
		setNoLicensePlate(!noLicensePlate);
		setShowResult(false);
		setResultBox(null);
		setShowSpinner(false);
		updateCarSearchIsValid(false);
	};

	return (
		<div className={classNames(styles.CarSearch)}>
			{!noLicensePlate && (
				<LicensePlateInput
					onChange={handleLicensePlateChange}
					hasError={licensePlateHasError}
				/>
			)}

			{showSpinner && !noLicensePlate && (
				<div className={classNames(styles.CarSearch__spinner)}>
					<LoadingSpinner
						color='primary'
						style='small'
					/>
				</div>
			)}

			{showResult && resultBox && !noLicensePlate && <ResultBox {...resultBox} />}

			<div className={classNames(styles.CarSearch__checkbox)}>
				<Checkbox
					name='checkbox'
					label='Jeg kender ikke min nummerplade'
					roundedCorners
					onChange={handleCheckboxChange}
				/>
			</div>

			{noLicensePlate && (
				<CarMakeAndModel
					contentType={contentType}
					makeSearchField={makeSearchField}
					yearSearchField={yearSearchField}
					modelSearchField={modelSearchField}
					variantSearchField={variantSearchField}
					themeName={themeName}
					saveOutputFieldsToForm={saveOutputFieldsToForm}
					saveFormValue={saveFormValue}
					register={register}
					stepContentId={stepContentId}
					updateCarSearchIsValid={updateCarSearchIsValid}
				/>
			)}
		</div>
	);
};
